import { PropertyValueMap, PropertyValues, TemplateResult, html, nothing, CSSResultGroup } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { DirectiveResult } from 'lit/directive';
import { ClassMapDirective } from 'lit/directives/class-map';
import { BlocksColumnSortEvent, BlocksRowCellSelectAllEvent } from '../../events';
import { KEYS } from '../../util/event-keys';
import { BlocksDispatchProps, BlocksElement } from '../BlocksElement';
import '../checkbox-field/checkbox-field';
import '../dropdown-panel/dropdown-panel';
import '../icon/icons/arrow-down-alt';
import '../icon/icons/arrow-up-alt';
import '../icon/icons/chevron-down';
import '../icon/icons/swap';
import { BlocksListItem } from '../list-item/list-item';
import '../skeleton/skeleton';
import type { SortDirection, TableHeaderAlignment, TableHeaderBehavior, TableHeaderBorderVariant } from './table-header-cell.model';
import styles from './table-header-cell.styles';

/**
 * @slot default - The table header cell content
 * @slot description - The table header cell description that displays below the main cell content
 * @slot filter - The table header cell filter content that appears in a dropdown panel if the behavior is set to filter
 * @fires bl-column-sort - emitted when behavior is filter (when wither ascending/descending is selected)
 * @fires bl-row-cell-select-all - emitted when select-all checkbox is checked
 * @fires bl-table-frozen-change - event is emitted when isFrozen prop value is updated
 * @csspart base - The component's base wrapper.
 * @csspart skeleton - The component's skeleton element.
 * @csspart checkbox - The component's checkbox element.
 * @csspart button - The component's button element.
 * @csspart heading - The component's heading wrapper.
 * @csspart sort-icon - The component's sort-icon wrapper.
 * @csspart title - The component's title wrapper.
 * @csspart part-default - The component's default slot.
 * @csspart icon-chevron-down - The component's chevron-down icon.
 * @csspart description-wrapper - The component's description wrapper.
 * @csspart part-description - The component's description slot.
 * @csspart dropdown-panel - The component's dropdown-panel element.
 * @csspart part-filter - The component's filter slot.
 * @csspart list - The component's list element.
 * @csspart ascending - The component's ascending list-item.
 * @csspart descending - The component's descending list-item.
 * @csspart filter-by - The component's filter-by list-item.
 */
export class BlocksTableHeaderCell extends BlocksElement {static styles: CSSResultGroup = styles;

  @property({ type: Number })
  dataKey: number | undefined;

  /**
   * Colspan
   * - The number of columns to span the table header cell
   */
  @property({ type: Number, reflect: true })
  colspan: number | undefined;

  /**
   * Width
   * - The width of the cell
   */
  @property({ type: String })
  width: string | undefined;

  /**
   * Border variants
   * <ul>
   * <li>**default** adds a border to the bottom of the cell</li>
   * <li>**top** adds a border to the top of the cell</li>
   * <li>**top-right** adds a border to the top right of the cell</li>
   * <li>**right** adds a border to the right of the cell</li>
   * <li>**bottom-none** removes the border to the bottom of the cell</li>
   * </ul>
   */
  @property({ type: String })
  border: TableHeaderBorderVariant;

  /**
   * Align variants
   * <ul>
   * <li>**right** renders a table cell with horizontally right aligned content</li>
   * <li>**center** renders a table cell with horizontally center aligned content</li>
   * </ul>
   */
  @property({ type: String })
  align: TableHeaderAlignment;

  /**
   * Behavior variant
   * <ul>
   * <li>**sortable** renders a button inside the cell to trigger sortable table columns</li>
   * <li>**filter** renders a button inside the cell to trigger dropdown panel to sort the table columns</li>
   * </ul>
   */
  @property({ type: String })
  behavior: TableHeaderBehavior;

  /**
   * Sort direction. To be used in only one header-cell at a time
   * <ul>
   * <li>**ascending** renders a arrow icon to represent ascending order of column cells</li>
   * <li>**descending** renders a arrow icon to represent descending order of column cells</li>
   * </ul>
   */
  @property({ type: String })
  sortDirection: SortDirection;

  /**
   * Frozen
   * - Makes the cell frozen on scroll
   */
  @property({ type: Boolean })
  isFrozen: boolean | undefined;

  /**
   * Sticky
   * - Makes the cell sticky on scroll
   */
  @property({ type: Boolean })
  isSticky: boolean | undefined;

  /**
   * Tooltip
   * - If true, adds a tooltip to the cell if the content goes over two lines
   * - _*This is dynamically applied and should not be manually added to the cell_
   */
  @property({ type: Boolean })
  tooltip: boolean | undefined;

  /**
   * Bulk actions
   * - If true, adds the checkbox for bulk actions
   * - _*This is dynamically applied and should not be manually added to the cell_
   */
  @property({ type: Boolean })
  isBulkAction: boolean | undefined;

  /**
   * Bulk action selected
   * - Sets the selected state
   * - _*This is dynamically applied and should not be manually added to the cell_
   */
  @property({ type: Boolean })
  isBulkActionSelected: boolean | undefined;

  /**
   * Bulk action indeterminate
   * - Sets the state indeterminate.
   * - _*This is dynamically applied and should not be manually added to the cell_
   */
  @property({ type: Boolean })
  isBulkActionIndeterminate: boolean | undefined;

  /**
   * Expandable
   * - If true, adds padding to left to align content with expandable rows
   * - _*This is dynamically applied and should not be manually added to the cell_
   */
  @property({ type: Boolean })
  isExpandable: boolean | undefined;

  /**
   * Draggable
   * - If true, adds padding to left to align content with draggable rows
   * - _*This is dynamically applied and should not be manually added to the cell_
   */
  @property({ type: Boolean })
  isDraggable: boolean | undefined;

  /**
   * Active
   * - Append an active state for the sorting on the button
   * - _*This is dynamically applied and should not be manually added to the cell_
   */
  @state()
  private isActive: boolean | undefined;

  /**
   * Active filter
   * - Append an active state for the filter on the button
   * - _*This is dynamically applied and should not be manually added to the cell_
   */
  @property({ type: Boolean })
  isActiveFilter = false;

  /**
   * Active Shadow
   * - Append an active state for the shadow on the table header cell
   * - _*This is dynamically applied and should not be manually added to the cell_
   */
  @property({ type: Boolean })
  isActiveShadow: boolean | undefined;

  /**
   * First
   * - Targets the first cell in a row
   * - _*This is dynamically applied and should not be manually added to the cell_
   */
  @property({ type: Boolean })
  isFirst: boolean | undefined;

  /**
   * Mobile view
   * - If true, adds an arrow to indicate the mobile view popover on small screens only
   * - _*This is dynamically applied and should not be manually added to the cell_
   */
  @property({ type: Boolean })
  isMobileView: boolean | undefined;

  /**
   * Loading State
   * - Enables the skeleton loader on cells
   * - _*This is dynamically applied and should not be manually added to the cell_
   */
  @property({ type: Boolean })
  isLoading = false;

  /**
   * Disabled State
   * - table-header-cell will be disabled when table-row is disabled.
   * - _*This is dynamically applied and should not be manually added to the cell_
   */
  @property({ type: Boolean })
  disabled: boolean | undefined;

  @query('.cat-c-table-header-cell')
  private _headerCellElement: HTMLElement | undefined;

  private setDirection = true;

  private toggleKeys = [KEYS.ESCAPE, KEYS.TAB, KEYS.ENTER, KEYS.SPACE];

  /**
   * Connected callback lifecycle
   * 1. Add mousedown event listener
   */
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('mousedown', this.handleOnClickOutside, false); /* 1 */
  }

  /**
   * Disconnected callback lifecycle
   * 1. Remove mousedown event listener
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('mousedown', this.handleOnClickOutside, false); /* 1 */
  }

  protected willUpdate(changedProperties: PropertyValues<this>) {
    if (changedProperties.has('behavior') && this.behavior) {
      if (changedProperties.has('sortDirection') && this.sortDirection && this.setDirection) {
        if (this.sortDirection === 'ascending') {
          this.sortAscending();
        } else {
          this.sortDescending();
        }
        this.isActive = true;
      } else {
        this.sortDefault();
      }
      this.setDirection = false;
    }
  }

  /**
   * Updated lifecycle hook
   * 1. If sortable, set the sort direction to ascending
   * 2. Fire to set the cell width
   * 3. Fire to set the cell empty attributes
   */

  updated(changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>) {
    if (changedProperties.has('isFrozen')) {
      this.dispatch({
        eventName: 'bl-table-frozen-change',
        detailObj: {
          value: true
        }
      });
    }
    if (changedProperties.has('isLoading') && !this.isLoading) {
      this.setWidth(); /* 2 */
      this.setEmpty(); /* 3 */
      this.dispatch({
        eventName: 'bl-table-header-loading',
        detailObj: {
          value: true
        }
      });
    }
  }

  firstUpdated() {
    this.setListItemRoles();
  }

  /**
   * Get the cat-list under header cell.
   */
  private getList() {
    return this.querySelector('cat-list') ?? this.shadowRoot?.querySelector('cat-dropdown-panel')?.querySelector('cat-list');
  }

  /**
   * 1. Get the list item under header cell
   * 2. Set the list role to listbox
   * 3. Get the list items based on the list, loop through and add list item role to option
   */
  private setListItemRoles() {
    if (this.behavior === 'filter') {
      /* 1 */
      const list = this.getList();
      if (list) {
        /* 2 */
        list.listRole = 'listbox';
        /* 3 */
        const listItems = list.querySelectorAll('cat-list-item');
        listItems.forEach((listItem: BlocksListItem) => {
          listItem.listItemRole = 'option';
        });
      }
    }
  }

  /**
   * Set the width
   * 1. Add inline width style to cell to adjust the width
   */
  private setWidth() {
    if (this.width) {
      const isPercentage = this.width.endsWith('%');
      if (!isPercentage && this._headerCellElement) {
        this._headerCellElement.style.minWidth = this.width;
        this._headerCellElement.style.width = this.width;
      } else {
        const tableObject = this.parentElement?.closest('cat-table-object')?.shadowRoot?.querySelector('.cat-c-table-object');
        if (tableObject) {
          const width = `${(parseInt(this.width) / 100) * tableObject.clientWidth}` + 'px';

          if (this._headerCellElement) {
            this._headerCellElement.style.width = width;
            this._headerCellElement.style.minWidth = width;
          }
        }
      }
    }
  }

  /**
   * Set the empty
   * 1. Add aria-hidden and a negative tabindex to prevent tabbing for proper A11y if the cell has not content.
   */
  private setEmpty = () => {
    if (this.innerHTML === '' || this.innerHTML === ' ') {
      this._headerCellElement?.setAttribute('aria-hidden', 'true');
      this._headerCellElement?.setAttribute('tabindex', '-1');
    }
  };

  /**
   * Toggle filter
   * 1. Toggles the active state between true and false
   * 2. If the active state is toggled to false, close the panel and return focus to the trigger
   */
  private toggleFilter() {
    /* 1 */
    this.isActiveFilter = !this.isActiveFilter;
  }

  /**
   * Default Sort
   * 1) Displays swap icon to indicate this header cell is sortable
   */
  private sortDefault() {
    this.sortDirection = null;
  }

  /**
   * Sort ascending
   * 1) Changes the sort direction to ascending
   */
  private sortAscending() {
    this.sortDirection = 'ascending';
  }

  /**
   * Sort descending
   * 1) Changes the sort direction to descending
   */
  private sortDescending() {
    this.sortDirection = 'descending';
  }

  /**
   * Toggle filter and list item sort
   * 1. Close the filter dropdown panel when a list item is clicked
   * 2. If list item value is ascending or descending then set the sort direction, else set to null
   * 3. Emit a custom event on click of each filter list item
   */
  private toggleFilterSort(listItem: BlocksListItem) {
    /* 1 */
    this.toggleFilter();
    if (listItem.value) {
      /* 2 */
      if (listItem.value === 'ascending') {
        this.sortDirection = 'ascending';
      } else if (listItem.value === 'descending') {
        this.sortDirection = 'descending';
      } else {
        this.sortDirection = null;
      }
      /* 3 */
      this.setActiveCell();
      const value = listItem.value.toString() as 'ascending' | 'descending' | null;
      this.eventEmit('bl-column-sort', value) as BlocksColumnSortEvent;
    }
  }

  /**
   * This event will fire on selecting filter from the dropdown on Keydown
   */
  private handleFilterOnKeydown(e: KeyboardEvent) {
    if (e.key === KEYS.ENTER || e.key === KEYS.SPACE) {
      e.preventDefault();
      this.toggleFilterSort(e.target as BlocksListItem);
    }
  }

  /**
   * This event will fire on clicking filter from the dropdown
   */
  private onClickingFilterOption(e: CustomEvent) {
    this.toggleFilterSort(e.target as BlocksListItem);
  }

  /**
   * Set behaviors
   * 1. On click of the table header, set the active state to true.
   * 2. If is sortable, toggle the sort direction.
   * 3. If has filter, toggle display of the dropdown panel.
   */
  private toggleBehaviors() {
    /* 1 */
    this.isActive = true;
    /* 2 */
    if (this.behavior === 'sortable') {
      if (this.sortDirection === 'ascending') {
        this.sortDescending();
      } else {
        this.sortAscending();
      }
      this.setActiveCell();
      if (this.sortDirection !== undefined) {
        this.eventEmit('bl-column-sort', this.sortDirection) as BlocksColumnSortEvent;
      }
    }
    /* 3 */
    if (this.behavior === 'filter') {
      this.toggleFilter();
    }
  }

  /**
   * Set active cell
   * 1. Any cell that is not the current selected cell, then remove isActive and reset sort direction.
   */
  private setActiveCell() {
    /* 1 */
    const element = this.closest('cat-table')?.querySelectorAll<BlocksTableHeaderCell>('cat-table-header-cell');

    if (element) {
      const allTableHeaderCells = Array.from(element).filter((el: HTMLElement) => el !== this);
      allTableHeaderCells.forEach((cell: BlocksTableHeaderCell) => {
        cell.isActive = false;
        if (cell.behavior) {
          cell.sortDefault();
        }
      });
    }
  }

  /**
   * Handle click outside the component
   * 1) Close the dropdown panel on click outside
   * 2) If the dropdown panel is already closed then we don't care about outside clicks and we
   * can bail early
   * 3) By the time a user clicks on the page the shadowRoot will almost certainly be
   * defined, but TypeScript isn't that trusting and sees this.shadowRoot as possibly
   * undefined. To work around that we'll check that we have a shadowRoot (and a
   * rendered .host) element here to appease the TypeScript compiler. This should never
   * actually be shown or run for a human end user.
   * 4) Check to see if we clicked inside the active dropdown panel
   * 5) If the dropdown panel is active and we've clicked outside of the panel then it should
   * be closed.
   */
  private handleOnClickOutside = (event: MouseEvent) => {
    /* 2 */
    if (!this.isActiveFilter) {
      return;
    }

    /* 3 */
    if (!this.shadowRoot?.host) {
      throw Error('Could not determine panel context during click handler');
    }

    /* 4 */
    const didClickInside = event.composedPath().includes(this.shadowRoot.host);

    /* 5 */
    if (this.isActiveFilter && !didClickInside) {
      this.toggleFilter();
    }
  };

  /**
   * Handle on keydown
   * 1) If the dropdown panel is open and escape or tab is keyed, close the menu and return focus to the trigger button
   */
  private handleKeyDown(e: KeyboardEvent) {
    if (this.isActiveFilter === true && this.toggleKeys.includes(e.key)) {
      e.preventDefault();
      this.toggleFilter();
      this.shadowRoot?.querySelector<HTMLButtonElement>('.cat-c-table-header-cell__button')?.focus();
    }
  }

  /**
   * 1. Once the filter panel is open state, press the arrow down should focus the first list item. Arrow up press will focus the last element
   * 2. Escape or tab close the filter panel. For Enter and space toggle the panel
   */
  private handleKeyDownOnHeader(e: KeyboardEvent) {
    /* 1 */
    if (this.isActiveFilter === true) {
      e.preventDefault();
      const listItems: BlocksListItem[] = Array.from(this.getList()?.querySelectorAll('cat-list-item') ?? [])?.filter((list) => !list.isStatic);
      if (e.key === KEYS.DOWN_ARROW) {
        listItems[0]?.focus();
      }
      if (e.key === KEYS.UP_ARROW) {
        listItems?.pop()?.focus();
      }
    }

    /* 2 */
    if (this.isActiveFilter === true && this.toggleKeys.includes(e.key)) {
      this.toggleFilter();
    }
  }

  /**
   * Event emit
   * @param name - name of the emit event
   * @param value - value of the emit event
   */
  private eventEmit(name: string, value: 'ascending' | 'descending' | null) {
    return this.dispatch({
      eventName: name,
      detailObj: {
        value: value
      }
    });
  }

  private renderLoading(componentClassName: DirectiveResult<typeof ClassMapDirective>) {
    return html`
      <div part="base" class="${componentClassName}">
        <cat-skeleton part="skeleton" exportparts=${this.getExportParts('skeleton')}></cat-skeleton>
      </div>
    `;
  }

  render(): TemplateResult<1> {
    const componentClassName = this.componentClassNames('cat-c-table-header-cell', {
      'cat-c-table-header-cell--align-right': this.align === 'right',
      'cat-c-table-header-cell--align-center': this.align === 'center',
      'cat-c-table-header-cell--sortable': this.behavior === 'sortable',
      'cat-c-table-header-cell--filter': this.behavior === 'filter',
      'cat-c-table-header-cell--ascending': this.sortDirection === 'ascending',
      'cat-c-table-header-cell--descending': this.sortDirection === 'descending',
      'cat-c-table-header-cell--frozen': this.isFrozen === true,
      'cat-c-table-header-cell--border-top': this.border === 'top',
      'cat-c-table-header-cell--border-top-right': this.border === 'top-right',
      'cat-c-table-header-cell--border-right': this.border === 'right',
      'cat-c-table-header-cell--border-bottom-none': this.border === 'bottom-none',
      'cat-c-table-header-cell--border-right-only': this.border === 'right-only',
      'cat-c-table-header-cell--tooltip': this.tooltip === true,
      'cat-c-table-header-cell--sticky': this.isSticky === true,
      'cat-is-bulk-action': this.isBulkAction === true,
      'cat-is-expandable': this.isExpandable === true,
      'cat-is-draggable': this.isDraggable === true,
      'cat-is-active': this.isActive === true,
      'cat-is-active-shadow': this.isActiveShadow === true,
      'cat-is-active-filter': this.isActiveFilter === true,
      'cat-is-first': this.isFirst === true,
      'cat-is-mobile-view': this.isMobileView === true,
      'cat-c-table-header-cell--loader': this.isLoading === true
    });
    if (this.isLoading) {
      return this.renderLoading(componentClassName);
    }
    return html`
      <div part="base" role="columnheader" class="${componentClassName}">
        ${this.behavior === 'sortable' || this.behavior === 'filter'
          ? html`
              ${this.isBulkAction === true
                ? html`
                    <cat-checkbox-field
                      part="checkbox"
                      exportparts=${this.getExportParts('checkbox-field')}
                      class="cat-c-table-header-cell__checkbox"
                      name="bulk-action-header-cell-checkbox"
                      ?hideLabel=${true}
                      .checked=${this.isBulkActionSelected}
                      ?indeterminate=${this.isBulkActionIndeterminate}
                      ?disabled=${this.disabled}
                      ?disableKeyboardNavigation=${true}
                      @bl-checkbox-change=${(e: Event) => {
                        const path: Array<unknown> = e.composedPath();
                        const isSelected: boolean = (path[0] as HTMLInputElement)?.checked;
                        this.dispatch({
                          e,
                          eventName: 'bl-row-cell-select-all',
                          detailObj: { isSelected }
                        } as BlocksDispatchProps) as BlocksRowCellSelectAllEvent;
                      }}
                    >
                      Bulk action table header cell checkbox
                    </cat-checkbox-field>
                  `
                : nothing}
              <button
                part="button"
                class="cat-c-table-header-cell__button"
                @click=${this.toggleBehaviors}
                @keydown=${this.handleKeyDownOnHeader}
                aria-expanded=${this.isActiveFilter === true ? true : false}
              >
                <span part="heading" class="cat-c-table-header-cell__heading">
                  ${this.sortDirection === 'ascending'
                    ? html`
                        <cat-icon-arrow-up-alt
                          part="sort-icon"
                          exportparts=${this.getExportParts('arrow-up-alt', 'icon')}
                          class="cat-c-table-header-cell__sortable-icon"
                        ></cat-icon-arrow-up-alt>
                      `
                    : this.sortDirection === 'descending'
                    ? html`
                        <cat-icon-arrow-down-alt
                          part="sort-icon"
                          exportparts=${this.getExportParts('arrow-down-alt', 'icon')}
                          class="cat-c-table-header-cell__sortable-icon"
                        ></cat-icon-arrow-down-alt>
                      `
                    : this.behavior === 'sortable'
                    ? html` <cat-icon-swap part="sort-icon" class="cat-c-table-header-cell__sortable-icon"></cat-icon-swap> `
                    : nothing}
                  <div part="title" class="cat-c-table-header-cell__title">
                    <slot part="part-default"></slot>
                  </div>
                  ${this.behavior === 'filter'
                    ? html`
                        <cat-icon-chevron-down
                          part="icon-chevron-down"
                          exportparts=${this.getExportParts('chevron-down', 'icon')}
                          class="cat-c-table-header-cell__filter-icon"
                        ></cat-icon-chevron-down>
                      `
                    : nothing}
                </span>
                ${this.slotNotEmpty('description') &&
                html`
                  <span part="description-wrapper" class="cat-c-table-header-cell__description">
                    <slot part="part-description" name="description"></slot>
                  </span>
                `}
              </button>
              ${this.behavior === 'filter'
                ? html`
                    <cat-dropdown-panel
                      part="dropdown-panel"
                      exportparts=${this.getExportParts('dropdown-panel')}
                      class="cat-c-table-header-cell__filter"
                      @keydown=${this.handleKeyDown}
                    >
                      ${this.slotNotEmpty('filter')
                        ? html`
                            <slot
                              part="part-filter"
                              name="filter"
                              @slotchange=${this.setListItemRoles}
                              @keydown=${this.handleFilterOnKeydown}
                              @bl-select=${this.onClickingFilterOption}
                            ></slot>
                          `
                        : html`
                            <cat-list
                              part="list"
                              exportparts=${this.getExportParts('list')}
                              @bl-select=${this.onClickingFilterOption}
                              @keydown=${this.handleFilterOnKeydown}
                            >
                              <cat-list-item
                                part="ascending"
                                exportparts=${this.getExportParts('list-item-ascending', 'list-item')}
                                value="ascending"
                              >
                                Sort A to Z
                              </cat-list-item>
                              <cat-list-item
                                part="descending"
                                exportparts=${this.getExportParts('list-item-descending', 'list-item')}
                                value="descending"
                              >
                                Sort Z to A
                              </cat-list-item>
                              <cat-list-item
                                part="filter-by"
                                exportparts=${this.getExportParts('list-item-filter-by', 'list-item')}
                                value="filter-by"
                              >
                                Filter by
                              </cat-list-item>
                            </cat-list>
                          `}
                    </cat-dropdown-panel>
                  `
                : nothing}
            `
          : html`
              ${this.isBulkAction === true
                ? html`
                    <cat-checkbox-field
                      part="checkbox"
                      exportparts=${this.getExportParts('checkbox-field')}
                      class="cat-c-table-header-cell__checkbox"
                      name="bulk-action-header-cell-checkbox"
                      ?hideLabel=${true}
                      .checked=${this.isBulkActionSelected}
                      ?indeterminate=${this.isBulkActionIndeterminate}
                      ?disabled=${this.disabled}
                      ?disableKeyboardNavigation=${true}
                      @bl-checkbox-change=${(e: Event) => {
                        const path: Array<unknown> = e.composedPath();
                        const isSelected: boolean = (path[0] as HTMLInputElement)?.checked;
                        this.dispatch({
                          e,
                          eventName: 'bl-row-cell-select-all',
                          detailObj: { isSelected }
                        } as BlocksDispatchProps) as BlocksRowCellSelectAllEvent;
                      }}
                    ></cat-checkbox-field>
                  `
                : nothing}
              <div part="title" class="cat-c-table-header-cell__title">
                <slot part="part-default"></slot>
              </div>
              ${this.slotNotEmpty('description') &&
              html`
                <span part="description-wrapper" class="cat-c-table-header-cell__description">
                  <slot part="part-description" name="description"></slot>
                </span>
              `}
            `}
      </div>
    `;
  }
}

if (customElements.get('cat-table-header-cell') === undefined) {
  customElements.define('cat-table-header-cell', BlocksTableHeaderCell);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-table-header-cell': BlocksTableHeaderCell;
  }
}
